import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@shared/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@shared/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@shared/components/ui/popover";
import { useDebounce } from '@shared/hooks/useDebounce';
import ThreeDotLoader from '@shared/components/ThreeDotLoader';

/**
 * Shared AsyncSelect component for reusable async select functionality
 * @param {Object} props - Component props
 * @param {string} props.value - Selected value
 * @param {function} props.onValueChange - Callback when value changes
 * @param {function} props.onItemSelect - Callback when item is selected
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.searchPlaceholder - Search input placeholder
 * @param {string} props.notFoundMessage - Message when no items found
 * @param {React.ReactNode} props.icon - Icon to display
 * @param {function} props.useSearchQuery - Hook for fetching search results
 * @param {function} props.extractItems - Function to extract items from query response
 * @param {function} props.getItemKey - Function to get unique key for each item
 * @param {function} props.getItemValue - Function to get value for each item
 * @param {function} props.getItemLabel - Function to get display label for each item
 * @param {function} props.formatSelectedLabel - Function to format selected item label
 * @param {number} props.debounceDelay - Debounce delay in ms (default: 300)
 * @param {number} props.searchLimit - Number of items to fetch (default: 50)
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.width - Width class (default: "w-full max-w-[400px]")
 * @param {boolean} props.disabled - Whether the select is disabled
 * @param {Object} props.queryParams - Additional query parameters to pass to the search hook
 * @param {boolean} props.showAllOption - Whether to show "All" option at the top
 * @param {string} props.allOptionLabel - Label for the "All" option
 * @param {string} props.allOptionValue - Value for the "All" option
 */
export function AsyncSelect({
  value,
  onValueChange,
  onItemSelect,
  placeholder,
  searchPlaceholder,
  notFoundMessage,
  icon,
  useSearchQuery,
  extractItems,
  getItemKey,
  getItemValue,
  getItemLabel,
  formatSelectedLabel,
  debounceDelay = 300,
  searchLimit = 50,
  className,
  disabled = false,
  queryParams = {},
  defaultItem = null,
  showAllOption = false,
  allOptionLabel = "All",
  allOptionValue = "",
  clientSideSearch = false,
  getItemSearchText = null,
}) {
  const [open, setOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");

  // Debounce the search term to avoid too many API calls
  const debouncedSearchTerm = useDebounce(searchTerm, debounceDelay);

  // If clientSideSearch is enabled, we always fetch with empty search term to get all items (or up to limit)
  // Otherwise, we use the specific search term
  const querySearchTerm = clientSideSearch ? "" : debouncedSearchTerm;

  // Use the provided search query hook
  const { data: queryResponse, isLoading, error } = useSearchQuery(
    querySearchTerm,
    searchLimit,
    1,
    open, // Only fetch when dropdown is open
    queryParams // Pass additional query parameters
  );

  // Extract items using provided function
  let items = extractItems ? extractItems(queryResponse) : [];

  // If clientSideSearch is enabled, filter items locally
  if (clientSideSearch && searchTerm && getItemSearchText) {
    const normalize = (text) =>
      text ? text.toString().toLowerCase().trim() : "";
    const term = normalize(searchTerm);

    items = items.filter((item) => {
      const searchText = getItemSearchText(item);
      return normalize(searchText).includes(term);
    });
  }

  // Handle popover state changes
  const handleOpenChange = (newOpen) => {
    setOpen(newOpen);
    if (!newOpen) {
      setSearchTerm(""); // Clear search when closing
    }
  };

  // Find selected item for display
  const selectedItem =
    items.find((item) => getItemValue(item) === value) ||
    (defaultItem && getItemValue(defaultItem) === value ? defaultItem : null);

  const isAllSelected = showAllOption && (value === allOptionValue || value === "");

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-label={placeholder}
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal",
            !value && "text-muted-foreground",
            disabled && "opacity-50 cursor-not-allowed",
            className
          )}
        >
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            {icon && (
              <div className="flex-shrink-0">
                {React.cloneElement(icon, {
                  className: "h-4 w-4 text-primary flex-shrink-0 rtl:ml-2 ltr:mr-2",
                })}
              </div>
            )}
            <span className="truncate">
              {isAllSelected ? (
                allOptionLabel
              ) : value && selectedItem ? (
                formatSelectedLabel ? (
                  formatSelectedLabel(selectedItem)
                ) : (
                  getItemLabel(selectedItem)
                )
              ) : (
                placeholder
              )}
            </span>
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 flex-shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            autoComplete="off"
            autoCorrect="off"
            spellCheck="false"
            className="h-9"
            placeholder={searchPlaceholder || placeholder}
            value={searchTerm}
            onValueChange={setSearchTerm}
          />
          <CommandList className="max-h-[200px]">
            {isLoading ? (
              <div className="flex items-center justify-center py-6">
                <ThreeDotLoader size="sm" />
              </div>
            ) : error ? (
              <div className="flex items-center justify-center py-6 text-center">
                <span className="text-sm text-destructive">
                  Error loading data
                </span>
              </div>
            ) : (
              <>
                <CommandEmpty>
                  <div className="py-6 text-center text-sm">
                    {notFoundMessage}
                  </div>
                </CommandEmpty>
                <CommandGroup>
                  {/* All Option */}
                  {showAllOption && (
                    <CommandItem
                      key="all-option"
                      value={allOptionValue}
                      onSelect={(currentValue) => {
                        const newValue = currentValue === value ? "" : allOptionValue;
                        onValueChange?.(newValue);
                        onItemSelect?.(currentValue === value ? null : null);
                        setOpen(false);
                        setSearchTerm("");
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          value === allOptionValue || value === "" ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <div className="flex-1 min-w-0">
                        <span className="truncate font-medium">{allOptionLabel}</span>
                      </div>
                    </CommandItem>
                  )}

                  {/* Regular Items */}
                  {items.map((item) => {
                    const itemValue = getItemValue(item);
                    const itemKey = getItemKey(item);
                    const itemLabel = getItemLabel(item);

                    return (
                      <CommandItem
                        key={itemKey}
                        value={itemValue}
                        onSelect={(currentValue) => {
                          const newValue =
                            currentValue === value ? "" : itemValue;
                          onValueChange?.(newValue);
                          onItemSelect?.(currentValue === value ? null : item);
                          setOpen(false);
                          setSearchTerm("");
                        }}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            value === itemValue ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <div className="flex-1 min-w-0">
                          {typeof itemLabel === "string" ? (
                            <span className="truncate">{itemLabel}</span>
                          ) : (
                            itemLabel
                          )}
                        </div>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
